/**
 * A line that types itself out, without the line ever being absent.
 *
 * The naive typewriter renders a growing slice of the string, which means for
 * most of its life the DOM contains a *truncated command* — and a truncated
 * command in a terminal mockup is a command that says something else. What a
 * crawler indexes, what a screen reader announces, and what sits in the
 * prerendered bytes must be the whole line.
 *
 * So the full text is always present and always accessible; the untyped tail is
 * merely made transparent, and `aria-hidden` keeps the caret out of the
 * announcement. Under reduced motion nothing is transparent and there is no
 * caret at all.
 */
import { useEffect, useState } from 'react'

import { useInViewOnce } from './useInViewOnce'
import { useReducedMotionSafe } from './motion'

export function Typewriter({
  text,
  cps = 42,
  delay = 0,
  className,
  caret = true,
}: {
  text: string
  /** Characters per second. */
  cps?: number
  delay?: number
  className?: string
  caret?: boolean
}) {
  const reduced = useReducedMotionSafe()
  const [ref, seen] = useInViewOnce<HTMLSpanElement>()
  const [typed, setTyped] = useState(reduced ? text.length : 0)

  useEffect(() => {
    if (reduced || !seen) return
    let frame = 0
    let started = 0
    const step = (now: number) => {
      if (!started) started = now
      const elapsed = (now - started) / 1000 - delay
      const n = Math.max(0, Math.min(text.length, Math.floor(elapsed * cps)))
      setTyped(n)
      if (n < text.length) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [cps, delay, reduced, seen, text])

  const done = typed >= text.length

  return (
    <span ref={ref} className={className}>
      {reduced || done ? (
        text
      ) : (
        <>
          <span>{text.slice(0, typed)}</span>
          <span className="kl-type__pending">{text.slice(typed)}</span>
        </>
      )}
      {caret && !reduced && !done ? <span className="kl-type__caret" aria-hidden="true" /> : null}
    </span>
  )
}
